import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ClipboardList, Shield } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from "recharts";

import { Alert, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { OpsHealthCard } from "@/components/OpsHealthCard";
import {
  getEntries,
  getFieldNoteCount,
  getOpenPunches,
  getPunchNotesReviewCount,
  getQbPushErrors,
  listPayPeriodApprovals,
} from "@/lib/api";
import { formatHours, hoursToHundredths, hundredthsToHours } from "@/lib/hoursMath";
import { getLastNDaysRange, groupEntriesByDay, groupHoursByDay } from "@/lib/time";
import { Metric } from "@/pages/dashboard/admin/Metric";
import { useAdminViewModel } from "@/pages/dashboard/admin/AdminViewModelContext";

function metricValue(loading: boolean, value: number | string | undefined): number | string {
  if (loading) return "…";
  if (value === undefined || value === null) return "—";
  return value;
}

export function AdminOverviewView() {
  const {
    user,
    isAdmin,
    canViewReports,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    filterCostCode,
    filterCustomer,
    filterEmployee,
    entriesCountData,
    correctionStats,
    correctionStatsLoading,
  } = useAdminViewModel();

  const overviewParams = React.useMemo(() => {
    const params = new URLSearchParams();
    if (filterCostCode !== "all") params.set("cost_code_id", filterCostCode);
    if (filterCustomer !== "all") params.set("customer_id", filterCustomer);
    if (filterEmployee !== "all") params.set("user_id", filterEmployee);
    if (fromDate) params.set("from_date", `${fromDate}T00:00:00`);
    if (toDate) params.set("to_date", `${toDate}T23:59:59`);
    params.set("limit", "500");
    params.set("offset", "0");
    return params;
  }, [filterCostCode, filterCustomer, filterEmployee, fromDate, toDate]);

  const overviewEntriesQ = useQuery({
    queryKey: ["entries", "overview", overviewParams.toString()],
    queryFn: () => getEntries(overviewParams),
    enabled: !!user && canViewReports,
  });
  const overviewEntries = overviewEntriesQ.data ?? [];

  const openQ = useQuery({
    queryKey: ["ops-open-punches"],
    queryFn: () => getOpenPunches(14),
    enabled: !!user && canViewReports,
    refetchInterval: 5 * 60 * 1000,
  });
  const qbErrQ = useQuery({
    queryKey: ["ops-qb-push-errors"],
    queryFn: () => getQbPushErrors(25),
    enabled: !!user && canViewReports,
    refetchInterval: 5 * 60 * 1000,
  });
  const unackedNotesQ = useQuery({
    queryKey: ["field-notes-count", "unprocessed"],
    queryFn: () => getFieldNoteCount({ processed: false, all: true }),
    enabled: !!user && canViewReports,
  });
  const unackedPunchNotesQ = useQuery({
    queryKey: ["punch-notes-review-count"],
    queryFn: () => getPunchNotesReviewCount(),
    enabled: !!user && canViewReports,
  });
  const ppaQ = useQuery({
    queryKey: ["ppa-team", "pending_manager"],
    queryFn: () => listPayPeriodApprovals("pending_manager"),
    enabled: !!user && canViewReports,
    refetchInterval: 5 * 60 * 1000,
  });

  const punchChartData = React.useMemo(
    () => groupEntriesByDay(overviewEntries).slice(-14),
    [overviewEntries],
  );
  const hoursChartData = React.useMemo(
    () => groupHoursByDay(overviewEntries).slice(-14),
    [overviewEntries],
  );
  const hoursWorked = hundredthsToHours(
    overviewEntries.reduce((sum, entry) => sum + hoursToHundredths(entry.duration || 0), 0),
  );
  const openNonExempt = (openQ.data?.open_punches ?? []).filter((p) => p.qb_payroll_code !== "E").length;
  const unackedNotes =
    (unackedNotesQ.data?.count ?? 0) + (unackedPunchNotesQ.data?.count ?? 0);
  const notesLoading = unackedNotesQ.isLoading || unackedPunchNotesQ.isLoading;

  return (
    <div className="mt-4 grid grid-cols-1 gap-4 [&>*]:min-w-0">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const r = getLastNDaysRange(7);
              setFromDate(r.from);
              setToDate(r.to);
            }}
          >
            Week
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const r = getLastNDaysRange(30);
              setFromDate(r.from);
              setToDate(r.to);
            }}
          >
            Month
          </Button>
        </div>
        <Input
          className="min-w-[160px] w-[160px]"
          type="date"
          value={fromDate}
          onChange={(event) => setFromDate(event.target.value)}
        />
        <Input
          className="min-w-[160px] w-[160px]"
          type="date"
          value={toDate}
          onChange={(event) => setToDate(event.target.value)}
        />
        <div className="text-xs text-muted-foreground pb-2">
          Range applies to punches, hours, charts, and corrections (same as Admin Report filters).
        </div>
      </div>

      {unackedNotes > 0 && (
        <Link to="/admin/field-notes" className="min-w-0">
          <Alert className="border-destructive/30 bg-destructive/10">
            <Shield className="h-4 w-4 text-destructive" />
            <AlertTitle className="text-destructive">Unacknowledged Field Notes</AlertTitle>
            <div className="text-sm text-destructive">
              There are <strong>{unackedNotes}</strong> field note(s) awaiting acknowledgment.
            </div>
          </Alert>
        </Link>
      )}
      {(ppaQ.data?.count ?? 0) > 0 && (
        <Link to="/admin/pay-period-reviews" className="min-w-0">
          <Alert className="border-warning/30 bg-warning/10">
            <ClipboardList className="h-4 w-4" />
            <AlertTitle>Pay period reviews pending</AlertTitle>
            <div className="text-sm">
              There are <strong>{ppaQ.data?.count}</strong> employee pay-period submission(s) awaiting review.
            </div>
          </Alert>
        </Link>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 [&>*]:min-w-0">
        <Metric title="Total punches" value={metricValue(false, entriesCountData?.count ?? 0)} />
        <Metric
          title="Hours worked"
          value={metricValue(overviewEntriesQ.isLoading, formatHours(hoursWorked))}
        />
        <Link to="/admin/corrections" className="min-w-0">
          <Metric
            title="Corrections"
            value={metricValue(correctionStatsLoading, correctionStats?.total ?? 0)}
          />
        </Link>
        <Metric
          title="Open punches (non-exempt)"
          value={metricValue(openQ.isLoading, openNonExempt)}
        />
        <Link to="/admin/field-notes" className="min-w-0">
          <Metric title="Unacked notes" value={metricValue(notesLoading, unackedNotes)} />
        </Link>
        <Link to="/admin/pay-period-reviews" className="min-w-0">
          <Metric
            title="Pay period reviews"
            value={metricValue(ppaQ.isLoading, ppaQ.data?.count ?? 0)}
          />
        </Link>
        <Metric
          title="QB push failures"
          value={metricValue(qbErrQ.isLoading, qbErrQ.data?.count ?? 0)}
        />
      </div>
      {overviewEntries.length >= 500 && (
        <div className="text-xs text-muted-foreground">
          Hours and charts use the first 500 punches in this range.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 [&>*]:min-w-0">
        <Card>
          <CardHeader>
            <CardTitle>Punches by Day</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={punchChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis allowDecimals={false} />
                <RTooltip />
                <Bar dataKey="punches" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Hours by Day</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hoursChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <RTooltip />
                <Bar dataKey="hours" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {canViewReports && <OpsHealthCard enabled={canViewReports} isAdmin={!!isAdmin} />}
    </div>
  );
}
